/**
 * Работа со временем в таймзоне локации.
 *
 * v2 отдаёт время в UTC с суффиксом Z. Legacy-панель вместо этого руками
 * прибавляла разницу с Москвой (moment-timezone плюс магическая константа 180) —
 * здесь вся арифметика идёт через Intl, таймзона берётся из ответа API.
 */

const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
] as const;

const WEEKDAYS = [
  "Воскресенье",
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
] as const;

const MINUTE_MS = 60_000;

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "short",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

/** Календарные поля момента `date` в таймзоне `timeZone`. */
export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts: Record<string, string> = {};
  for (const { type, value } of partsFormatter(timeZone).formatToParts(date)) {
    parts[type] = value;
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Intl в некоторых движках отдаёт полночь как 24 — нормализуем.
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: WEEKDAY_INDEX[parts.weekday] ?? 0,
  };
}

/** Смещение таймзоны в миллисекундах для конкретного момента. */
function offsetMs(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone);
  const asUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * Локальные календарные поля → момент времени.
 * Две итерации закрывают переходы на летнее время.
 */
export function zonedTime(
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
): Date {
  const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
  let guess = new Date(naive - offsetMs(new Date(naive), timeZone));
  guess = new Date(naive - offsetMs(guess, timeZone));
  return guess;
}

/** Полночь того дня (в таймзоне), на который приходится `date`, плюс `addDays`. */
export function startOfDay(date: Date, timeZone: string, addDays = 0): Date {
  const p = zonedParts(date, timeZone);
  return zonedTime(timeZone, p.year, p.month, p.day + addDays, 0, 0);
}

/** Момент `HH:mm` того же дня, что и `date` (плюс `addDays`). */
export function atTimeOfDay(
  date: Date,
  timeZone: string,
  hhmm: string,
  addDays = 0,
): Date {
  const [hour, minute] = hhmm.split(":").map(Number);
  const p = zonedParts(date, timeZone);
  return zonedTime(timeZone, p.year, p.month, p.day + addDays, hour, minute);
}

/** Совпадает ли календарная дата двух моментов в этой таймзоне. */
export function isSameZonedDay(a: Date, b: Date, timeZone: string): boolean {
  const pa = zonedParts(a, timeZone);
  const pb = zonedParts(b, timeZone);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
}

/** Выходной ли день (суббота или воскресенье). */
export function isWeekend(date: Date, timeZone: string): boolean {
  const day = zonedParts(date, timeZone).weekday;
  return day === 0 || day === 6;
}

/** Пояс, пока API не отдал пояс локации: все локации сейчас в Москве. */
export const FALLBACK_TZ = "Europe/Moscow";

/** «2026-09-11» — сегодня по стенным часам зоны (не UTC: с 00 до 03 МСК это вчера). */
export function todayIso(timeZone: string): string {
  const p = zonedParts(new Date(), timeZone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** «09:05» */
export function formatHHmm(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
}

/** «9:05» — час без ведущего нуля. */
export function formatHmm(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${p.hour}:${String(p.minute).padStart(2, "0")}`;
}

/** «29 июля 2026» */
export function formatDayMonthYear(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${p.day} ${MONTHS_GENITIVE[p.month - 1]} ${p.year}`;
}

/** «Среда» */
export function formatWeekday(date: Date, timeZone: string): string {
  return WEEKDAYS[zonedParts(date, timeZone).weekday];
}

/** Разница в целых минутах (a минус b), вниз. */
export function diffMinutes(a: Date, b: Date): number {
  return Math.floor((a.getTime() - b.getTime()) / MINUTE_MS);
}

/** Сколько миллисекунд до начала следующей минуты — чтобы тикать без дрейфа. */
export function msUntilNextMinute(now: Date = new Date()): number {
  return MINUTE_MS - (now.getTime() % MINUTE_MS);
}
